import { createBrowserRouter, Navigate } from 'react-router-dom'
import { RootLayout } from '@/components/layout/RootLayout'
import { AppLayout } from '@/components/layout/AppLayout'
import { ProtectedRoute } from '@/components/layout/ProtectedRoute'
import { LoginPage } from '@/pages/LoginPage'
import { OnboardingPage } from '@/pages/OnboardingPage'
import { DashboardPage } from '@/pages/DashboardPage'
import { BirthChartPage } from '@/pages/BirthChartPage'
import { DashaPage } from '@/pages/DashaPage'
import { MuhurtaPage } from '@/pages/MuhurtaPage'
import { EventMuhurtaPage } from '@/pages/EventMuhurtaPage'
import { CompatibilityPage } from '@/pages/CompatibilityPage'
import { SettingsPage } from '@/pages/SettingsPage'
import { InsightsPage } from '@/pages/InsightsPage'
import { RemediesPage } from '@/pages/RemediesPage'
import { DevDashboardPage } from '@/pages/DevDashboardPage'
import { AdminLayout } from '@/pages/admin/AdminLayout'
import { AdminUsersPage } from '@/pages/admin/AdminUsersPage'
import { AdminContentPage } from '@/pages/admin/AdminContentPage'
import { AdminHealthPage } from '@/pages/admin/AdminHealthPage'
import { DailyTimingsPage } from '@/pages/DailyTimingsPage'
import { ErrorPage } from '@/pages/ErrorPage'
import { LandingPage } from '@/pages/LandingPage'
import { PricingPage } from '@/pages/PricingPage'
import { BillingPage } from '@/pages/BillingPage'
import { CheckoutSuccessPage } from '@/pages/CheckoutSuccessPage'
import { TermsPage } from '@/pages/TermsPage'
import { PrivacyPage } from '@/pages/PrivacyPage'
import { useIsAuthenticated, useIsAuthLoading } from '@/store'

// Smart home route: shows LandingPage for guests, redirects to /dashboard for authenticated users
function SmartRootRoute() {
  const isAuthenticated = useIsAuthenticated()
  const isAuthLoading = useIsAuthLoading()

  if (isAuthLoading) {
    return (
      <div className="min-h-screen bg-cosmic-void flex items-center justify-center">
        <div className="w-10 h-10 rounded-full border-2 border-cosmic-mist animate-spin-slow" />
      </div>
    )
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />
  }

  return <LandingPage />
}

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    errorElement: <ErrorPage />,
    children: [
      // Home: LandingPage for guests, /dashboard redirect for authenticated users
      {
        path: '/',
        element: <SmartRootRoute />,
      },
      {
        path: '/pricing',
        element: <PricingPage />,
      },
      {
        // Post-Stripe checkout return URL — must match successUrl in SubscriptionService.createCheckoutSession()
        path: '/billing/success',
        element: (
          <ProtectedRoute>
            <CheckoutSuccessPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/login',
        element: <LoginPage />,
      },
      {
        path: '/terms',
        element: <TermsPage />,
      },
      {
        path: '/privacy',
        element: <PrivacyPage />,
      },
      {
        // Onboarding is outside AppLayout — full-screen flow
        path: '/onboarding',
        element: (
          <ProtectedRoute>
            <OnboardingPage />
          </ProtectedRoute>
        ),
      },
      // Pathless layout route — wraps all authenticated app pages in AppLayout
      {
        element: (
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        ),
        children: [
          { path: 'dashboard',      element: <DashboardPage /> },
          { path: 'daily-timings',  element: <DailyTimingsPage /> },
          { path: 'birth-chart',    element: <BirthChartPage /> },
          { path: 'dasha',          element: <DashaPage /> },
          { path: 'muhurta',        element: <MuhurtaPage /> },
          { path: 'event-muhurta',  element: <EventMuhurtaPage /> },
          { path: 'compatibility',  element: <CompatibilityPage /> },
          { path: 'insights',       element: <InsightsPage /> },
          { path: 'remedies',       element: <RemediesPage /> },
          { path: 'settings',       element: <SettingsPage /> },
          { path: 'billing',        element: <BillingPage /> },
          { path: 'developer',      element: <DevDashboardPage /> },
        ],
      },
      {
        path: '/admin',
        element: (
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        ),
        children: [
          { index: true, element: <Navigate to="/admin/users" replace /> },
          { path: 'users',   element: <AdminUsersPage /> },
          { path: 'content', element: <AdminContentPage /> },
          { path: 'health',  element: <AdminHealthPage /> },
        ],
      },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
])
